/**
 * 픽시 렌더러.
 *
 * 시뮬레이션을 읽기만 한다. 여기서 계산한 값은 절대 시뮬레이션으로 돌아가지 않는다.
 * 부동소수점 보간과 Math.random을 마음껏 써도 되는 유일한 층이다.
 *
 * 스프라이트는 아틀라스 한 장을 공유하므로 수백 개를 그려도 드로우콜이 몇 개로 묶인다.
 */
import { Application, Container, Graphics, Sprite, Texture, TilingSprite } from 'pixi.js';
import { FP_ONE, toPx } from '../sim/fixed.ts';
import { lobArc, lobScale } from './lob.ts';
import { Cell, TILE_PX } from '../sim/grid.ts';
import { MAX_PROJECTILES, TICK_HZ, Team } from '../sim/units.ts';
import { BranchCode, Role } from '../sim/troops.ts';
import type { World } from '../sim/world.ts';
import { SKILL_CODE } from '../sim/skills.ts';

/** 함성에 사기가 꺾인 적 머리 위 ▼가 보이는 시간(ms). */
const SHAKEN_MS = 1300;
import { MAX_SQUADS, MAX_WAYPOINTS } from '../sim/commands.ts';
import { lookTarget } from '../game/path-drag.ts';
import type { DragLook } from '../game/path-drag.ts';
import {
  HERO_CHIEF_KEY,
  HERO_CHIEF_FOOT_KEY,
  SQUAD_COLORS,
  HERO_LEADER_FOOT_KEY,
  HERO_LEADER_KEY,
  bannerKey,
  buildAtlas,
  buildEdgeFadeTexture,
  buildGroundTexture,
  buildForestTexture,
  buildFordTexture,
  buildHillTexture,
  buildMarshTexture,
  buildRockTexture,
  buildStakesTexture,
  buildWallTexture,
  companionFootKey,
  companionKey,
  troopSpriteKey,
} from './atlas.ts';
import {
  DAMAGE_DEALT_COLOR,
  DAMAGE_LEADER_COLOR,
  DAMAGE_TAKEN_COLOR,
  DamageFloats,
  scanDamage,
} from './damage-floats.ts';
import type { HpSeen } from './damage-floats.ts';
import { Dir, attackFrameAt, loadUnitSheet } from './unit-sheet.ts';
import { ShieldView, loadShieldSheet } from './shields.ts';
import type { ShieldSheet } from './shields.ts';
import type { HandPos, UnitFrames, UnitSheet } from './unit-sheet.ts';
import { cameraOrigin, screenDirToWorld, screenToWorld as turnScreenToWorld, worldToScreen as turnWorldToScreen } from './turn.ts';
import { foeLeaderOf } from '../meta/foe-leaders.ts';
import { ApproachView, approachShift } from './approach.ts';
import { FaceLook, faceShift } from './face-look.ts';
import { WIDE_ZOOM, baseZoom } from './zoom.ts';

/**
 * 싸우는 동안의 배율(`render/zoom.ts`): 폰 1.5배, 넓은 화면 2배. 모듈을 읽을 때 한 번 정한다 — 판 도중에 창 크기가
 * 폰의 문턱을 넘나들어도(PC에서 창을 줄임) 배율은 그대로다. 다음에 페이지를 열 때 다시 정한다.
 */
const ZOOM = typeof window === 'undefined' ? WIDE_ZOOM : baseZoom(window.innerWidth, window.innerHeight);
/**
 * 화면 층의 그림(데미지 숫자)과 안개 덩어리의 크기를 정하는 기준 배율. 카메라의 배율(`ZOOM`)과 따로 둔다 — 폰의 배율을
 * 낮추며 함께 낮추면 숫자가 4분의 3으로 작아져 읽히지 않는다. 숫자는 병사와 함께 줄이지 않는다.
 */
const ART_ZOOM = WIDE_ZOOM;
/**
 * 전장을 끌어 경로를 그리는 동안의 배율. 넓게 보여야 멀리 도는 길을 그린다(플레이한 사람이 청했다) — 2배에서 폰
 * 세로 화면은 월드 폭 206px, 전장의 7분의 1이었다. 1.25면 1.6배 넓다. 도트 그림은 이 배율에서 조금 거칠어지지만
 * 끄는 동안뿐이다.
 */
const DRAG_ZOOM = 1.25;
/** 배율이 목표로 옮겨 가는 빠르기(프레임당 몫). */
const ZOOM_LERP = 0.12;
/** 다시 보기에서 이 배율보다 물러나면 피해 숫자를 그리지 않는다. */
const SPECTATE_FLOAT_ZOOM = 0.9;
const MAX_DECALS = 220;
const MAX_SPARKS = 64;
const SPARK_FRAMES = 7;
/** 한 프레임에 새로 튈 수 있는 불꽃 수. 난전에서 비용이 폭주하지 않게 묶어둔다. */
const SPARK_BUDGET = 10;
/**
 * 열린 위아래 가장자리 너머로 땅을 더 까는 폭(픽셀). 거기가 출구로 보이게 한다. 카메라도 여기까지 내려간다.
 *
 * 48이었다. 판을 열 때 화면을 물리면(`ApproachView`, 1배까지) 폰 세로에서 장수가 화면 끝에서 30% 안에 서야 하는데
 * (`APPROACH_KEEP`), 장수가 선 줄(아래 가장자리에서 170px)과 이 폭을 더한 218px이 화면 높이의 26%뿐이라 카메라가 더
 * 내려가지 못했다 — 판을 연 첫 1초 동안 장수가 함성 단추와 조이스틱 뒤에 있었다(QA). 88이면 258px(30%)이다.
 */
const EDGE_BLEED = 88;
/** 아군 체력 막대의 폭과, 스프라이트 꼭대기에서 막대까지(월드 픽셀). 화면에서는 지금 배율(`zoom`)배다 — 축소하면 함께 준다. */
const HP_BAR_W = 10;
const HP_BAR_TOP = 3;
/** 카메라가 장수를 따라가는 빠르기(프레임당 남은 거리의 몫). */
const FOLLOW_LERP = 0.18;
/**
 * 끄는 동안 앞을 보는 카메라의 빠르기. 손가락 밑 땅이 너무 빨리 흐르지 않게 느리게 둔다.
 * 처음에는 장수를 따를 때와 같은 0.18이었고, 플레이한 사람이 "너무 빠르다"고 했다. 0.05로 낮췄다가, 끄는 동안
 * 축소(`DRAG_ZOOM`)를 넣은 뒤 다시 "경로 지정 시 속도가 너무 빠르다"고 해 0.035로 더 낮췄다.
 */
const LOOK_LERP = 0.035;
/**
 * 그림 시트(`unit-sheet.ts`)의 한 픽셀을 월드 몇 픽셀로 그리나. 넓은 화면의 배율(2배)에서 0.5면 시트 한 픽셀이 화면 한
 * 픽셀이다(폰의 1.5배에서는 0.75픽셀 — 고른 맞바꿈이다, `render/zoom.ts`) —
 * 정수배라야 도트가 뭉개지지 않는다. 시트 인물의 키(발~정수리 약 36)가 월드 18픽셀로, 예전 코드 그림(28칸)과 비슷하다.
 */
const SHEET_SCALE = 0.5;
/** 걷기 한 박자(서기 → A → 서기 → B). 시안(art/mock)과 같다. */
const WALK_BEAT_MS = 170;
/** 이 틱 사이에 이만큼(고정소수) 넘게 움직였으면 걷는 그림을 쓴다. 제자리에서 떨리는 병사가 걷지 않게. */
const WALK_MIN_FP = FP_ONE / 8;
/** 쓰러짐 1·2의 길이. 그다음은 쓰러짐 3(누움)으로 남는다. */
const DEATH_STEP_MS = 150;
/** 치기 몇 틱 전부터 준비 칸을 보이는가(3틱 = 0.1초 ≈ 공격 한 칸). */
const WINDUP_TICKS = 3;
/** 시체가 누워 있는 시간과, 그 뒤 사라지는 시간. 수백 명이 쓰러지는 전장에서 끝없이 쌓이지 않게. */
const CORPSE_MS = 15000;
/** 부서진 방패가 기울어 떨어지며 사라지는 시간(ms). */
const SHIELD_BREAK_MS = 450;
const CORPSE_FADE_MS = 2000;
/** 시체는 서 있는 병사보다 늘 아래에 깐다(y 정렬 값에서 이만큼 뺀다). */
const CORPSE_Z = -100000;
/**
 * 장수의 휩쓸기(정면 반원을 한꺼번에 친다, `World.leaderStrike`) 칼바람. 여럿이 한꺼번에 쓰러지는 까닭이
 * 보이지 않아 "범위 공격이냐"는 물음이 나왔다. 칼끝이 반원을 긋는 데 SWEEP, 사라지기까지 전체 SLASH.
 */
const SLASH_SWEEP_MS = 90;
const SLASH_MS = 260;
/** 칼바람의 가운데 높이: 발(스프라이트 기준점)보다 이만큼 위 — 허리께. */
const SLASH_LIFT = 6;
/** 가로 화면에서 카메라를 도는 각. 월드의 위(적 쪽, -y)가 화면의 오른쪽이 된다. */
const LANDSCAPE_TURN = Math.PI / 2;

/**
 * 이 GPU가 받는 가장 큰 텍스처 변. 병사 시트(2176)보다 작으면 시트를 쓰지 않는다 — 올리다 실패하면 빈 병사가 된다.
 * 알 수 없으면 WebGL2가 보장하는 2048로 친다(그러면 시트를 쓰지 않고 예전 그림으로 그린다).
 */
function maxTextureSize(app: Application): number {
  const r = app.renderer as unknown as {
    gl?: WebGLRenderingContext;
    gpu?: { device?: { limits?: { maxTextureDimension2D?: number } } };
  };
  if (r.gl) return r.gl.getParameter(r.gl.MAX_TEXTURE_SIZE) as number;
  return r.gpu?.device?.limits?.maxTextureDimension2D ?? 2048;
}

/** 야습에 우리가 볼 수 있는 거리(칸). 횃불(군수품)이면 더 멀리. 화면만의 값이다 — 시뮬레이션은 보지 않는다. */
export const NIGHT_SIGHT = 5;
export const NIGHT_SIGHT_TORCH = 8;

/**
 * 움직임을 줄인 사람인가. 빗줄기를 멈춰 그린다(`drawRain`). 값이 아니라 질의를 들고 있다가 그릴 때마다 읽는다 — 처음에는
 * 불러올 때 한 번만 읽어, 전투 중에 설정을 켜도 빗줄기가 계속 움직였다(리뷰가 짚었다).
 */
const REDUCED_MOTION = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;

export class Renderer {
  readonly app: Application;
  private readonly world: World;
  private readonly camera = new Container();
  private readonly hearAt = { x: 0, y: 0 };
  private readonly groundLayer = new Container();
  private readonly decalLayer = new Container();
  private readonly shadowLayer = new Container();
  private readonly actorLayer = new Container();
  private readonly fxLayer = new Container();
  /**
   * 화면 좌표로 그리는 층(카메라 밖): 체력 막대와 데미지 숫자. 가로 화면에서 카메라가 돌아도 늘 똑바로
   * 서야 하고 "머리 위"가 늘 화면 위여야 한다. 월드 좌표로 그리면 가로 화면에서 막대가 세로로 서고 숫자가
   * 옆으로 떠올랐다.
   */
  private readonly screenLayer = new Container();
  /**
   * 비(전장 특성 `Trait.Rain`)가 오는 판이면 화면에 빗줄기를 긋는다(`setRain`). 화면 좌표라 가로 화면에서도 늘 위에서
   * 아래로 내린다. 시뮬레이션과 무관한 그림이라 `performance.now`로 움직인다. 칩(HUD)만으로는 비가 오는 줄 몰랐다.
   */
  private readonly rainGfx = new Graphics();
  /**
   * 안개 덩어리(`drawFog`). **전장 좌표에** 그린다(카메라 아래) — 처음에는 화면 층에 그려, 전장이 스크롤돼도 덩어리가
   * 화면에 붙어 움직이지 않았다(플레이한 사람이 짚었다: "맵이 스크롤 되어도 타원들은 스크린에 고정으로 움직이지 않아서
   * 어색해"). 전장 전체를 희게 덮는 옅은 막은 그대로 화면 층(`rainGfx`)이다 — 어디를 보든 같은 안개다.
   */
  private readonly fogGfx = new Graphics();
  private rain = false;
  /** 안개(전장 특성 `Trait.Fog`): 전장을 희게 덮고 옅은 덩어리가 천천히 흐른다(`setFog`). 그림뿐이다. */
  private fog = false;
  /**
   * 야습(`Trait.Night`): 화면을 어둡게 하고, 우리 누구에게서도 `NIGHT_SIGHT_CELLS`칸 밖에 있는 적은 그리지 않는다(`setNight`).
   * 그림뿐이다 — 시뮬레이션의 적은 그대로 보고 싸운다(밤의 규칙은 궁수 사거리뿐). 횃불(군수품)이면 더 멀리 보인다.
   */
  private readonly nightGfx = new Graphics();
  private night = 0;
  private seen: Uint8Array = new Uint8Array(0);
  private seenAt = -1;
  /**
   * **가로 화면이면 그리는 쪽만 90도 돌린다.** 시뮬레이션은 늘 "위로 진격"이고, 가로 화면에서는 그것이
   * 왼쪽에서 오른쪽으로 보인다. 입력은 돌려 놓고 커맨드로 싣는다(`InputState.rotated`) — 리플레이에는
   * 늘 같은 방향의 커맨드가 실리므로 서버 검증·랭킹은 화면 방향과 무관하다. 플레이한 사람이 청했다.
   * 병사 그림·지형 타일은 돌리지 않는다(거꾸로 돌려 세운다) — 바위 그림자는 늘 아래에 진다.
   */
  rotated = false;
  /** 거꾸로 돌려 세울 지형 타일. 방향이 바뀔 때 한꺼번에 돌린다. */
  private readonly uprights: Sprite[] = [];

  private frames!: ReadonlyMap<string, Texture>;
  private markTex!: Texture[];
  private bloodTex!: Texture;
  private sparkTex!: Texture;
  private arrowTex!: Texture;
  /** 곡사 화살의 땅 그림자(`syncArrows`가 프레임마다 다시 그린다). */
  private readonly lobShadows = new Graphics();
  /** 방패(`World.shield`). 0 = 아군, 1 = 적. */
  private shieldTex!: Texture[];
  /** 유닛마다 방패 그림. 병사 그림과 따로 그려, 부서지면 방패만 사라진다. 처음 방패가 보일 때 만든다. */
  private readonly shieldSprites: (Sprite | null)[] = [];
  /** 지난 프레임에 본 방패 값. 줄면 막은 불꽃, 0이 되면 부서짐. 화면 전용. */
  private readonly shieldSeen: Int16Array;
  /** 방패가 부서진 시각(ms). 그 뒤 잠깐 조각이 흩어지며 사라진다. 0이면 부서지는 중이 아니다. */
  private readonly shieldBrokeAt: Float64Array;
  private bannerTex!: Texture[];

  private readonly sprites: (Sprite | null)[] = [];
  private readonly shadows: (Sprite | null)[] = [];
  private readonly wasAlive: Uint8Array;
  private readonly arrows: Sprite[] = [];
  private readonly decals: Sprite[] = [];
  private decalCursor = 0;
  private sparkBudget = 0;
  private readonly sparks: Sprite[] = [];
  private readonly sparkLife = new Int16Array(MAX_SPARKS);
  private sparkCursor = 0;
  /** 직전 프레임의 피격 표시. 새로 맞은 순간에만 불꽃을 튀긴다. */
  private readonly prevFlash: Uint8Array;
  /**
   * 공격 동작: 직전에 본 쿨다운과 공격을 시작한 시각(ms, 0이면 없음). 시뮬레이션은 치는 틱에 `cd`를 `sCd`로 채우고 틱마다
   * 하나씩 뺀다 — 쿨다운이 **올라간** 것을 보면 방금 쳤다. 시뮬레이션에 렌더링용 칸을 새로 두지 않아 리플레이가 그대로다.
   */
  private readonly prevCd: Int16Array;
  private readonly attackAt: Float64Array;
  /**
   * 공격을 시작한 순간의 방향. 찌르는 동안에는 이 방향을 쓴다 — 매 프레임 바라보는 쪽으로 다시 고르면, 표적이 몸을
   * 가로질러 돌 때 같은 찌르기의 앞 칸은 왼쪽, 뒤 칸은 오른쪽 그림으로 튀었다(리뷰가 짚었다).
   */
  private readonly attackDir: Uint8Array;
  private readonly banners: (Sprite | null)[] = [];
  /** 남은 이동경로를 잇는 선. 프레임마다 지우고 다시 긋는다. */
  private readonly pathGfx = new Graphics();
  /**
   * 아군 병사의 체력 막대. 프레임마다 지우고 다시 긋는다. 병사를 작게 가면서 한 명 한 명이
   * 이름을 가진 사람이 됐다 — 누가 위험한지 보여야 뺄지 말지를 정한다.
   */
  private readonly hpGfx = new Graphics();
  /** 체력 막대 자리를 재는 그릇. 프레임마다 병사 수만큼 새로 만들지 않게. */
  private readonly hpAt = { x: 0, y: 0 };

  /** 그림 시트. 없으면(불러오지 못했으면) 예전 코드 그림으로 그린다. */
  private sheet: UnitSheet | null = null;
  /** 방패 그림(`shields.ts`). 없으면 코드로 그린 옛 방패(`shieldTex`). */
  private shieldSheet: ShieldSheet | null = null;
  /** 병사마다 시트 그림(없으면 null — 예전 그림). */
  private readonly unitFrames: (UnitFrames | null)[] = [];
  /** 병사마다 마지막으로 본 방향. 비스듬히 설 때 방향이 떨리지 않게 이어 쓴다. */
  private readonly dir: Uint8Array;
  /** 쓰러지기 시작한 시각(ms). 0이면 살아 있거나 시체가 사라졌다. */
  private readonly deathAt: Float64Array;
  /** 휩쓸기 칼바람. 휘두를 때마다 하나 생겨 SLASH_MS 뒤에 사라진다. */
  private readonly slashGfx = new Graphics();
  /** 부대 고르기 중 지휘관 둘레의 고리 — 여기서 끌면 경로다. */
  private readonly pickGfx = new Graphics();
  /** 함성: 장수에게서 퍼지는 금빛 고리. */
  private readonly roarGfx = new Graphics();
  /** 함성의 기세가 오른 병사 발밑의 금빛 고리(몸 아래)와 머리 위로 떠오르는 불티(몸 위). `syncBuffs`. */
  private readonly buffRing = new Graphics();
  private readonly buffMotes = new Graphics();
  private seenRoars = 0;
  private readonly aidGfx = new Graphics();
  private readonly seenAid = [0, 0, 0, 0];
  private readonly aidAt = [-Infinity, -Infinity, -Infinity, -Infinity];
  private roarAt = -Infinity;
  /** 사기가 크게 꺾인 적 머리 위의 가라앉는 ▼. `syncShaken`. */
  private readonly shakenGfx = new Graphics();
  /** 유닛마다 마지막으로 본 `World.shakenTick`과, 그걸 본 때(ms). 새 틱이 보이면 그 유닛의 ▼를 다시 시작한다. */
  private shakeSeen = new Int32Array(0);
  private shakeAt = new Float64Array(0);
  private readonly slashes: { t0: number; ang: number; reach: number; flip: boolean }[] = [];
  /** 직전 프레임까지 본 휩쓸기 횟수(`World.strikeCount`). 늘었으면 그만큼 휘둘렀다. */
  private seenStrikes = 0;
  private slashFlip = false;
  /** 맞은 자리에서 떠오르는 데미지 숫자. */
  private readonly floats = new DamageFloats();
  /** 병사마다 직전 프레임의 체력·장수에게 받은 피해 누계와, 그때 살아 있었는지(`scanDamage`). */
  private readonly hpSeen: HpSeen;
  /** 다음 프레임에 체력을 읽기 시작할 병사. 한도에 걸려 미룬 병사부터 본다. */
  private dmgCursor = 0;

  constructor(app: Application, world: World) {
    this.app = app;
    this.world = world;
    this.wasAlive = new Uint8Array(world.alive.length);
    this.prevFlash = new Uint8Array(world.alive.length);
    this.prevCd = new Int16Array(world.alive.length);
    this.attackAt = new Float64Array(world.alive.length);
    this.attackDir = new Uint8Array(world.alive.length);
    this.dir = new Uint8Array(world.alive.length);
    this.deathAt = new Float64Array(world.alive.length);
    this.shieldSeen = new Int16Array(world.alive.length);
    this.shieldBrokeAt = new Float64Array(world.alive.length);
    // 지금 전장의 체력을 기준으로 삼는다. 0에서 시작하면 첫 화면을 그리기 전에 지난 틱의 피해가 빠진다
    // (루프는 첫 render 전에 틱을 돌릴 수 있다 — 리뷰가 짚었다).
    this.hpSeen = {
      hp: Int32Array.from(world.hp),
      leader: Int32Array.from(world.leaderDealt),
      live: Uint8Array.from(world.alive),
    };
  }

  static async create(world: World, host: HTMLElement): Promise<Renderer> {
    const app = new Application();
    await app.init({
      background: '#2b2a24',
      antialias: false,
      resizeTo: host,
      roundPixels: true,
    });
    host.appendChild(app.canvas);
    const r = new Renderer(app, world);
    r.sheet = await loadUnitSheet(maxTextureSize(app));
    r.shieldSheet = await loadShieldSheet();
    r.setup();
    return r;
  }

  private setup(): void {
    const atlas = buildAtlas();
    this.frames = atlas.frames;
    this.markTex = [this.frames.get('mark0')!, this.frames.get('mark1')!, this.frames.get('mark2')!, this.frames.get('mark3')!];
    this.bloodTex = this.frames.get('blood')!;
    this.sparkTex = this.frames.get('spark')!;
    this.arrowTex = this.frames.get('arrow')!;
    this.shieldTex = [this.frames.get('shield0')!, this.frames.get('shield1')!];
    this.bannerTex = [];
    for (let sq = 0; sq < MAX_SQUADS; sq++) this.bannerTex.push(this.frames.get(bannerKey(sq))!);

    const w = this.world.grid.widthPx;
    const h = this.world.grid.heightPx;

    // 땅은 위아래로 조금 더 깐다. 열린 가장자리 너머로 길이 이어지는 것처럼 보이게.
    const ground = new TilingSprite({
      texture: buildGroundTexture(),
      width: w,
      height: h + EDGE_BLEED * 2,
    });
    ground.y = -EDGE_BLEED;
    this.groundLayer.addChild(ground);

    // 지형 타일은 한 번만 만들어 두고 다시 건드리지 않는다. 지형은 전투
    // 중에 변하지 않는다.
    const rock = buildRockTexture();
    const wall = buildWallTexture();
    const byCell: Partial<Record<Cell, Texture>> = {
      [Cell.Forest]: buildForestTexture(),
      [Cell.Marsh]: buildMarshTexture(),
      [Cell.Hill]: buildHillTexture(),
      [Cell.Ford]: buildFordTexture(),
      [Cell.Stakes]: buildStakesTexture(),
    };
    const { cols, rows } = this.world.grid;
    for (let cy = 0; cy < rows; cy++) {
      for (let cx = 0; cx < cols; cx++) {
        const cell = this.world.grid.cellAt(cx, cy);
        let tex: Texture | undefined;
        if (cell === Cell.Blocked) {
          // 벽은 좌우에만 있다. 위아래 줄의 막힌 칸은 가장자리가 아니라 그냥 바위다.
          const edge = cx === 0 || cx === cols - 1;
          tex = edge ? wall : rock;
        } else {
          tex = byCell[cell];
        }
        if (!tex) continue;
        const s = new Sprite(tex);
        // 같은 그림을 그대로 깔면 격자가 눈에 보인다. 칸 좌표로 좌우를 뒤집어 무늬를 깬다.
        // **위아래는 뒤집지 않는다.** 바위·언덕·숲은 위에서 빛을 받아 아래에 그림자가 지게
        // 그렸다. 처음에는 상하도 뒤집어 넷으로 늘렸는데, 그러면 칸 절반의 바위가 그림자를
        // 머리에 이고 뒤집혀 보였다(플레이한 사람이 짚었다).
        s.anchor.set(0.5, 0.5);
        s.scale.set((cx * 7 + cy * 3) % 2 === 0 ? 1 : -1, 1);
        s.x = cx * TILE_PX + TILE_PX / 2;
        s.y = cy * TILE_PX + TILE_PX / 2;
        this.groundLayer.addChild(s);
        this.uprights.push(s);
      }
    }

    // 열린 가장자리의 옅어짐. 전장 안쪽 반 칸까지 걸쳐 경계가 선으로 보이지 않게 한다.
    const fadeH = EDGE_BLEED + TILE_PX / 2;
    const top = new Sprite(buildEdgeFadeTexture(fadeH, 'top'));
    top.width = w;
    top.height = fadeH;
    top.y = -EDGE_BLEED;
    const bottom = new Sprite(buildEdgeFadeTexture(fadeH, 'bottom'));
    bottom.width = w;
    bottom.height = fadeH;
    bottom.y = h + EDGE_BLEED - fadeH;
    this.groundLayer.addChild(top, bottom);

    for (let i = 0; i < MAX_DECALS; i++) {
      const s = new Sprite(this.bloodTex);
      s.anchor.set(0.5, 0.5);
      s.visible = false;
      this.decals.push(s);
      this.decalLayer.addChild(s);
    }

    for (let i = 0; i < MAX_SPARKS; i++) {
      const s = new Sprite(this.sparkTex);
      s.anchor.set(0.5, 0.5);
      s.visible = false;
      this.sparks.push(s);
      this.fxLayer.addChild(s);
    }

    // 곡사 화살의 땅 그림자. 화살보다 먼저 붙여 아래에 깔린다.
    this.fxLayer.addChild(this.lobShadows);
    for (let i = 0; i < MAX_PROJECTILES; i++) {
      const s = new Sprite(this.arrowTex);
      s.anchor.set(0.5, 0.5);
      s.visible = false;
      this.arrows.push(s);
      this.fxLayer.addChild(s);
    }

    // y값으로 앞뒤를 가린다. 덩어리가 겹쳐 보이는 느낌은 여기서 나온다.
    this.actorLayer.sortableChildren = true;

    this.fxLayer.addChild(this.pathGfx, this.slashGfx, this.pickGfx, this.roarGfx, this.aidGfx, this.buffMotes, this.shakenGfx);
    // 병사 그림자(`shadowLayer`)는 유닛이 날 때마다 뒤에 붙어 고리를 덮었다(리뷰가 짚었다). 그림자 위·몸 아래 제 층에 둔다.
    // 모든 층을 한 번에 순서대로 붙인다. 한때 병사 층이 붙기 전에 그 자리를 찾아(`getChildIndex`) 끼웠더니 전장이
    // 열리지 않았다(QA가 짚었다 — 'The supplied Container must be a child of the caller').
    this.camera.addChild(this.groundLayer, this.decalLayer, this.shadowLayer, this.buffRing, this.actorLayer, this.fxLayer, this.fogGfx);
    this.camera.scale.set(this.zoom);
    // 데미지 숫자의 글자는 월드 픽셀 크기로 구웠다. 화면 층에서도 기준 배율(`ART_ZOOM`)로 키워 예전과 같은 크기로. 경로를 끄느라
    // 축소한 동안에도 이 크기 그대로 둔다 — 숫자는 읽혀야 하므로 병사와 함께 줄이지 않는다.
    this.floats.layer.scale.set(ART_ZOOM);
    this.screenLayer.addChild(this.nightGfx, this.rainGfx, this.hpGfx, this.floats.layer);
    this.app.stage.addChild(this.camera, this.screenLayer);
  }

  /** alpha는 직전 틱과 현재 틱 사이의 보간 비율이다. */
  render(alpha: number): void {
    const w = this.world;
    this.sparkBudget = SPARK_BUDGET;
    const now = performance.now();
    this.orient();
    this.updateSight();
    for (let i = 0; i < w.count; i++) {
      this.syncUnit(i, alpha, now);
    }
    // 밤이면 보이지 않는 적의 몸을 감춘다 — 몸을 보고 그리는 효과(사기 흔들림 ▼ 등)보다 먼저. 처음에는 맨 끝에 두어, 적장이
    // 쓰러질 때 시야 밖 적 머리 위에 ▼가 떠 자리가 드러났다(리뷰가 짚었다).
    this.hideInDark();
    this.syncSlash(now);
    this.syncPickRing(now);
    this.syncRoar(now);
    this.syncAid(now);
    this.syncBuffs(now);
    this.syncShaken(now);
    this.fadeSparks();
    this.syncArrows(alpha);
    this.syncBanners();
    // 화면 층(체력 막대·숫자)은 이번 프레임의 카메라로 투영한다. 카메라를 나중에 옮기면 한 프레임씩 밀려 떨렸다.
    this.followCamera(alpha);
    this.syncDamage(now);
    this.drawHpBars();
    this.drawFog(now);
    this.drawRain(now);
  }

  /** 밤에 보이지 않는 적인가(`hideInDark`가 잰 칸). 낮이면 늘 false. */
  private unseen(i: number): boolean {
    if (this.night <= 0 || this.world.team[i] !== Team.Enemy) return false;
    const g = this.world.grid;
    const cx = Math.floor(toPx(this.world.x[i]) / TILE_PX);
    const cy = Math.floor(toPx(this.world.y[i]) / TILE_PX);
    return !g.inBounds(cx, cy) || this.seen[cy * g.cols + cx] !== 1;
  }

  /** 밤인가(`Trait.Night`)와 볼 수 있는 거리(칸). 0이면 낮이다. 전투를 열 때 한 번 정한다. */
  setNight(sightCells: number): void {
    this.night = sightCells;
    this.nightGfx.clear();
    if (sightCells <= 0) return;
    const g = this.world.grid;
    this.seen = new Uint8Array(g.cols * g.rows);
  }

  /**
   * 밤: 화면을 어둡게 깔고, 우리 누구에게서도 보이지 않는 적의 그림(몸·발밑 고리·방패)을 감춘다. 보이는 칸은 몇 프레임에
   * 한 번 다시 잰다 — 적마다 우리 모두와 거리를 재면 칠백 명끼리 곱이 된다. 칸으로 재어(우리 병사마다 둘레를 칠한다) 한 판
   * 칠백 명이면 칠만 칸쯤이다.
   */
  private hideInDark(): void {
    if (this.night <= 0) return;
    const w = this.world;
    const f = this.nightGfx;
    f.clear();
    f.rect(0, 0, this.app.screen.width, this.app.screen.height).fill({ color: 0x0b1020, alpha: 0.45 });
    // 쓰러진 적의 시체도 같은 판정으로 감춘다. 그 자리가 보이게 되면 시체도 다시 보인다 — 한 번 감춘 시체를 되돌리지 않아
    // 다가가도 끝내 안 보였다(리뷰가 짚었다).
    for (let i = 0; i < w.count; i++) {
      if (w.team[i] !== Team.Enemy) continue;
      const s = this.sprites[i];
      if (!this.unseen(i)) {
        if (w.alive[i] === 0 && this.deathAt[i] > 0 && s && !s.visible) s.visible = true;
        continue;
      }
      if (s) s.visible = false;
      const sh = this.shadows[i];
      if (sh) sh.visible = false;
      const sp = this.shieldSprites[i];
      if (sp) sp.visible = false;
    }
  }

  /**
   * 밤에 보이는 칸을 잰다(몇 틱에 한 번). **유닛을 그리기 전에** 부른다 — 쓰러진 적의 핏자국을 남길지(`syncUnit`)와 몸을
   * 감출지(`hideInDark`)가 같은 칸을 봐야 한다. 처음에는 감추는 쪽에서만 재어, 잰 틱 경계에 옛 칸으로 핏자국을 남기고 새
   * 칸으로 시체를 감춰 핏자국이 자리를 드러냈다(리뷰가 짚었다).
   */
  private updateSight(): void {
    if (this.night <= 0) return;
    const w = this.world;
    const g = w.grid;
    if (this.seenAt < 0 || w.tick - this.seenAt >= 6) {
      this.seenAt = w.tick;
      this.seen.fill(0);
      const r = this.night;
      for (let i = 0; i < w.count; i++) {
        if (w.alive[i] === 0 || w.team[i] !== Team.Player) continue;
        const cx = Math.floor(toPx(w.x[i]) / TILE_PX);
        const cy = Math.floor(toPx(w.y[i]) / TILE_PX);
        for (let dy = -r; dy <= r; dy++) {
          const y = cy + dy;
          if (y < 0 || y >= g.rows) continue;
          for (let dx = -r; dx <= r; dx++) {
            const x = cx + dx;
            if (x < 0 || x >= g.cols || dx * dx + dy * dy > r * r) continue;
            this.seen[y * g.cols + x] = 1;
          }
        }
      }
    }
  }

  /** 비가 오는 판인가(`Trait.Rain`). 전투를 열 때 한 번 정한다. */
  setRain(on: boolean): void {
    this.rain = on;
    if (!on && !this.fog) this.rainGfx.clear();
  }

  setFog(on: boolean): void {
    this.fog = on;
    if (!on && !this.rain) this.rainGfx.clear();
    if (!on) this.fogGfx.clear();
  }

  /**
   * 안개 덩어리: 전장 넓이에 맞춘 수의 큰 타원이 전장 좌표에서 옆으로 천천히 흐르다 전장 끝을 넘으면 반대편에서 돌아온다.
   * 자리·크기는 색인에서 뽑는다(빗줄기와 같은 까닭 — 난수면 프레임마다 흩어진다). 카메라 아래라 스크롤·확대·가로 화면
   * 돌리기를 땅과 함께 따른다. 크기는 예전 화면 픽셀 크기를 확대 배율로 나눈 전장 픽셀이다(보이는 크기가 예전과 같게).
   */
  private drawFog(now: number): void {
    if (!this.fog) return;
    const g = this.fogGfx;
    g.clear();
    const grid = this.world.grid;
    const W = grid.cols * TILE_PX;
    const H = grid.rows * TILE_PX;
    // 기준 그림 배율(`ART_ZOOM`)로 잰다 — 지금 배율(`this.zoom`)은 경로를 그릴 때 줄었다 돌아오며 바뀌어, 그것으로 재면 덩어리가 숨 쉬듯 커졌다 줄었다.
    const z = ART_ZOOM;
    const drift = REDUCED_MOTION?.matches === true ? 0 : now / 1000;
    // 예전 화면 한 장(덩어리 일곱)과 같은 짙기가 되게, 전장 넓이를 기준 화면 한 장(1280×800) 넓이로 나눈 만큼. 창 크기로 재면
    // 창을 바꿀 때 덩어리 수가 바뀌어 자리가 뛰었다.
    const screenArea = (1280 / z) * (800 / z);
    const n = Math.max(7, Math.min(40, Math.round((7 * W * H) / Math.max(1, screenArea))));
    // 가로 화면은 카메라가 90° 돌아 있다(`orient`) — 전장의 가로가 화면의 세로다. 덩어리가 화면에서 늘 옆으로 길고 옆으로
    // 흐르게, 돌아 있으면 흐르는 축과 긴 축을 전장의 세로로 바꾼다. 바꾸지 않았더니 가로 화면에서 덩어리가 세로로 섰다.
    const along = this.rotated ? H : W;
    const across = this.rotated ? W : H;
    const span = along + 400 / z;
    for (let k = 0; k < n; k++) {
      const hx = ((k * 7919) % 1000) / 1000;
      const hy = ((k * 104729) % 1000) / 1000;
      const a = ((((hx * span + (drift * (12 + (k % 7) * 3)) / z) % span) + span) % span) - 200 / z;
      const b = hy * across;
      const long = (180 + (k % 7) * 20) / z;
      const short = (60 + (k % 7) * 8) / z;
      if (this.rotated) g.ellipse(b, a, short, long);
      else g.ellipse(a, b, long, short);
      g.fill({ color: 0xeef0ee, alpha: 0.12 });
    }
  }

  /**
   * 빗줄기: 화면 넓이에 맞춘 수의 짧은 사선. 줄마다 정해진 자리(색인에서 뽑은 값)에서 시간만큼 내려와 화면 아래로 나가면
   * 위로 돌아온다. 움직임을 줄인 사람(`prefers-reduced-motion`)에게는 멈춘 채 옅게 그린다.
   */
  private drawRain(now: number): void {
    if (!this.rain && !this.fog) return;
    const g = this.rainGfx;
    g.clear();
    const W = this.app.screen.width;
    const H = this.app.screen.height;
    if (this.fog) {
      // 안개: 화면 전체를 희게 덮는다. 흐르는 덩어리는 전장 좌표에 따로 그린다(`drawFog`).
      g.rect(0, 0, W, H).fill({ color: 0xdfe3e2, alpha: 0.2 });
      if (!this.rain) return;
    }
    // 전장을 옅게 가라앉힌다. 빗줄기만으로는 밝은 땅에서 잘 안 보였다.
    g.rect(0, 0, W, H).fill({ color: 0x2a3440, alpha: 0.12 });
    const n = Math.min(260, Math.floor((W * H) / 5200));
    const still = REDUCED_MOTION?.matches === true;
    const t = still ? 0 : now / 1000;
    for (let k = 0; k < n; k++) {
      // 색인에서 뽑은 자리·빠르기. 난수(Math.random)를 쓰면 프레임마다 흩어져 깜빡인다.
      const hx = ((k * 7919) % 1000) / 1000;
      const hy = ((k * 104729) % 1000) / 1000;
      const speed = 520 + ((k * 31) % 7) * 40;
      const x = hx * (W + 40) - 20;
      const y = (((hy * (H + 40) + t * speed) % (H + 40)) + (H + 40)) % (H + 40) - 20;
      g.moveTo(x, y).lineTo(x - 3, y + 11);
    }
    g.stroke({ width: 1, color: 0xc8d6e6, alpha: still ? 0.25 : 0.4 });
  }

  /**
   * 화면이 가로인지 보고 카메라를 돌린다. 바뀔 때만 일한다(폰을 돌리면 전투 중에도 바뀐다). 전투를 열 때
   * 첫 틱 전에도 부른다(`main.ts`) — 그래야 첫 틱의 입력부터 새 방향으로 싣는다.
   */
  orient(): void {
    const want = this.app.screen.width > this.app.screen.height;
    if (want === this.rotated) return;
    this.rotated = want;
    this.camera.rotation = want ? LANDSCAPE_TURN : 0;
    for (const s of this.uprights) s.rotation = want ? -LANDSCAPE_TURN : 0;
    // 새 방향에서 카메라를 곧장 제자리에(따라가는 보간은 방향이 바뀐 첫 프레임에 엉뚱한 곳에서 출발한다).
    this.camCX = -1;
    this.camCY = -1;
  }

  /** 화면에서 "위"인 월드 방향. 세로 화면은 -y, 가로 화면은 -x(월드의 왼쪽이 화면의 위다). */
  private upX(): number {
    return this.rotated ? -1 : 0;
  }

  private upY(): number {
    return this.rotated ? 0 : -1;
  }

  /** 월드 픽셀 → 화면 픽셀. 카메라의 자리·배율·회전을 그대로 편다(픽시의 toGlobal과 같고 할당이 없다). */
  worldToScreen(x: number, y: number, out: { x: number; y: number }): void {
    turnWorldToScreen(this.rotated, this.camera.x, this.camera.y, this.zoom, x, y, out);
  }

  /**
   * 전장의 한 자리가 어디서 들리는가(소리 전용, `audio/battle-mix.ts`): 화면에서의 좌우(-1~1)와 **보는 사람**에게서
   * 떨어진 거리(전장 px). 좌우는 **화면**으로 잰다 — 가로 화면에서는 전장의 세로가 화면의 가로다.
   *
   * 거리는 카메라가 따라가는 사람(`watched` — 조종하는 장수, 고르는 중이면 그 지휘관, 다시 보기면 보는 사람)에게서
   * 전장의 길이로 잰다. 처음에는 화면 한가운데에서 쟀는데, 카메라는 그 사람을 가운데 두지 않는다 — 다가오는 장면은
   * 적 쪽으로, 평소에는 향한 쪽으로, 경로를 끄는 동안은 손가락 쪽으로 화면을 민다. 그러면 장수 곁의 칼 소리가
   * 화면이 밀린 만큼 작아졌다(플레이한 사람: "거리에 따른 소리 크기, 포커스 중인 장수 중심으로 해줘"). 따라갈 사람이
   * 없으면(다시 보기의 "전체", 장수가 쓰러진 뒤) 화면 한가운데에서 잰다 — 그때는 배율로 나눠 전장의 길이로 돌린다
   * (화면 픽셀로 재면 화면을 물릴 때마다 모든 소리가 가까워져 커진다).
   */
  hear(x: number, y: number, out: { pan: number; dist: number }): void {
    this.worldToScreen(x, y, this.hearAt);
    const hw = this.app.screen.width / 2;
    const hh = this.app.screen.height / 2;
    const dx = this.hearAt.x - hw;
    out.pan = hw > 0 ? Math.max(-1, Math.min(1, dx / hw)) : 0;
    const w = this.world;
    const u = this.watched();
    if (u >= 0 && w.alive[u] === 1) {
      out.dist = Math.hypot(x - toPx(w.x[u]), y - toPx(w.y[u]));
      return;
    }
    const dy = this.hearAt.y - hh;
    out.dist = Math.sqrt(dx * dx + dy * dy) / (this.zoom || 1);
  }

  /**
   * 카메라가 따라가는 사람: 다시 보기의 보는 사람, 부대를 고르는 중이면 그 지휘관, 아니면 조종하는 사람(장수 또는 고른
   * 분대의 동료, `controlled`), 그도 없으면 장수. 없으면 -1. 카메라(`followCamera`)와 귀(`hear`)가 같은 사람을 쓴다.
   */
  private watched(): number {
    const w = this.world;
    const sp = this.spectate;
    if (sp) return sp.unit;
    const f = this.pickFocus();
    if (f >= 0) return f;
    const c = w.controlled();
    return c >= 0 ? c : w.leaderIdx;
  }

  private spriteFor(i: number): Sprite {
    let s = this.sprites[i] ?? null;
    if (s) return s;
    const w = this.world;
    const spec = w.specs[w.specIdx[i]];
    const mounted = spec.branch === BranchCode.Horse;
    // 그림 시트가 있고 그 인물의 그림이 있으면 그것으로. 적군은 붉은 천을 초록으로 바꾼 본을 쓴다.
    // 적의 대장·부장은 인물마다 다른 차림이다(`meta/foe-leaders.ts` → `looks.foeLeaderLook`). 같은 몸(스펙)이라
    // 스펙만으로는 가르지 못한다 — 누구인지는 나라와 부대 번호에서 나온다.
    const lead = this.sheet?.keyForFoeLeader ? foeLeaderOf(w, i) : null;
    const art = this.sheet ? ((lead ? this.sheet.keyForFoeLeader!(lead.id, spec) : null) ?? this.sheet.keyFor(spec)) : null;
    const frames = art ? this.sheet!.frames(art, w.team[i] === Team.Enemy) : null;
    this.unitFrames[i] = frames;
    if (frames) {
      s = new Sprite(frames.stand[Dir.Front]);
      s.anchor.set(0.5, this.sheet!.footAnchor);
      s.scale.set(SHEET_SCALE);
      this.sprites[i] = s;
      this.actorLayer.addChild(s);
      this.addMark(i, mounted);
      return s;
    }
    const key =
      spec.role === Role.Leader
        ? // 말이 없으면 걷는 장수로 그린다. 가지(기병/보병)는 착장이 정했다.
          spec.branch === BranchCode.Horse
          ? HERO_LEADER_KEY
          : HERO_LEADER_FOOT_KEY
        : spec.role === Role.Chief
          ? // 산길에서 말에서 내린 적장은 걷는다(`dismount`). 시트의 차림(`lookFor`)과 같은 규칙이다.
            spec.branch === BranchCode.Horse
            ? HERO_CHIEF_KEY
            : HERO_CHIEF_FOOT_KEY
          : spec.role === Role.Companion
            ? // 동료도 말이 없으면 걷는다(`dressCompanion`).
              spec.branch === BranchCode.Horse
              ? companionKey(Math.max(1, w.squad[i]))
              : companionFootKey(Math.max(1, w.squad[i]))
            : troopSpriteKey(spec.culture, spec.branch, spec.tier);
    const tex = this.frames.get(key);
    if (!tex) throw new Error(`아틀라스에 없는 스프라이트입니다: ${key}`);
    s = new Sprite(tex);
    s.anchor.set(0.5, mounted ? 0.86 : 0.75);
    this.sprites[i] = s;
    this.actorLayer.addChild(s);
    this.addMark(i, mounted);
    return s;
  }

  /** 발밑 표식(피아 구분 고리). */
  private addMark(i: number, mounted: boolean): void {
    // 우리 용사는 금빛 고리(`mark2`), 적의 용사는 붉은 고리 안의 금빛 고리(`mark3`).
    const elite = this.world.specs[this.world.specIdx[i]].elite === true;
    const sh = new Sprite(this.markTex[this.world.team[i] === Team.Enemy ? (elite ? 3 : 1) : elite ? 2 : 0]);
    sh.anchor.set(0.5, 0.5);
    sh.scale.set(mounted ? 1.25 : 1);
    this.shadows[i] = sh;
    this.shadowLayer.addChild(sh);
  }

  /**
   * 바라보는 방향 → 시트의 네 방향. 가로·세로 중 뚜렷이 큰 쪽으로 고르고, 비스듬하면 직전 방향을 이어 쓴다 —
   * 대각선으로 걷는 병사가 틱마다 정면·옆모습을 오가며 깜빡이지 않게.
   */
  private facing(i: number): Dir {
    // 화면에서 본 방향으로 고른다. 가로 화면이면 월드 방향을 카메라처럼 90도 돌린다 — 적 쪽(월드 위)을 보는
    // 병사는 화면 오른쪽을 본다.
    const fx = this.rotated ? -this.world.faceY[i] : this.world.faceX[i];
    const fy = this.rotated ? this.world.faceX[i] : this.world.faceY[i];
    const ax = Math.abs(fx);
    const ay = Math.abs(fy);
    let d = this.dir[i] as Dir;
    if (ay > ax * 1.2) d = fy > 0 ? Dir.Front : Dir.Back;
    else if (ax > ay * 1.2) d = fx < 0 ? Dir.Left : Dir.Right;
    else if (ax + ay > 0) {
      // 비스듬하다. 직전 방향이 지금 사분면과 맞으면 이어 쓰고, 아니면 큰 축으로 고른다. 처음에는 이어 쓰기만
      // 해서, 처음부터 대각선으로 걷는 병사가 초기값(정면)에 붙어 뒤로 가면서도 정면을 봤다(리뷰가 짚었다).
      const fits =
        (d === Dir.Front && fy > 0) || (d === Dir.Back && fy < 0) || (d === Dir.Left && fx < 0) || (d === Dir.Right && fx > 0);
      if (!fits) d = ay >= ax ? (fy > 0 ? Dir.Front : Dir.Back) : fx < 0 ? Dir.Left : Dir.Right;
    }
    this.dir[i] = d;
    return d;
  }

  /** 시트 그림의 한 프레임: 피격 → 공격(방금 쳤고 공격 칸이 있으면) → 걷기(움직일 때) → 서기. */
  private pickFrame(i: number, fr: UnitFrames, now: number): Texture {
    const w = this.world;
    const d = this.facing(i);
    // hitFlash는 4에서 틱마다 준다. 앞 두 틱은 움찔, 뒤 두 틱은 되돌아오는 그림.
    if (w.hitFlash[i] > 2) return fr.hit;
    if (w.hitFlash[i] > 0) return fr.hit2;
    if (fr.attack && this.attackAt[i] > 0) {
      const ad = this.attackDir[i] as Dir;
      const row = fr.attack[ad];
      const k = attackFrameAt(now - this.attackAt[i], this.sheet!.attackMs, row.length);
      if (k >= 0) {
        this.dir[i] = ad;
        return row[k];
      }
      this.attackAt[i] = 0;
    }
    // 치기 직전(쿨다운이 거의 끝났고 표적이 닿는 거리): 준비 칸을 미리 든다. 공격 판정이 아니라 **예고**다 — 시뮬레이션과 같은
    // 판정을 흉내 낼 뿐이라 빗나가도(표적이 물러나 치지 않으면) 준비 자세가 잠깐 보였다 풀린다. 궁수는 활 사거리(`sRange`)로
    // 재므로 근접으로 바꾼 뒤에도 먼 표적에 활을 드는데, 활을 들어 겨누는 예고로는 오히려 맞다.
    if (fr.attack && (this.sheet?.attackHit ?? 0) > 0 && w.cd[i] > 0 && w.cd[i] <= WINDUP_TICKS) {
      const e = w.target[i];
      if (e >= 0 && w.alive[e] === 1) {
        const reach = w.sRange[i] + w.sRadius[e];
        const dx = w.x[e] - w.x[i];
        const dy = w.y[e] - w.y[i];
        if (dx * dx + dy * dy <= reach * reach) {
          this.dir[i] = d;
          return fr.attack[d][0];
        }
      }
    }
    const moved = Math.abs(w.x[i] - w.prevX[i]) + Math.abs(w.y[i] - w.prevY[i]);
    if (moved > WALK_MIN_FP && !this.frozen) {
      // 병사마다 박자를 조금씩 어긋나게 해 대열이 한 몸처럼 발을 맞추지 않게.
      const beat = Math.floor((now + i * 53) / WALK_BEAT_MS) & 3;
      if (beat === 1) return fr.walkA[d];
      if (beat === 3) return fr.walkB[d];
    }
    return fr.stand[d];
  }

  /**
   * 방패(`World.shield`)를 바라보는 쪽에 든다. 정면·옆을 보면 몸 앞에, 뒤를 보면 몸 뒤에 가린다. 막을 때마다 작은
   * 불꽃, 다 닳아 부서지면 조각이 튀며(불꽃 둘) 잠깐 기울어 떨어지다 사라진다.
   * 자리는 화면에서 잡는다(몸 가운데에서 옆·위로 몇 픽셀). 가로 화면이면 카메라가 돌았으므로 그 오프셋을 월드로 돌려 놓는다.
   */
  private syncShield(i: number, px: number, py: number, body: Sprite, fr: UnitFrames | null, now: number): void {
    const w = this.world;
    const cur = w.shield[i];
    const seen = this.shieldSeen[i];
    let sp = this.shieldSprites[i] ?? null;
    if (cur <= 0 && seen <= 0 && this.shieldBrokeAt[i] === 0) {
      if (sp && sp.visible) sp.visible = false;
      return;
    }
    if (!sp) {
      sp = new Sprite(this.shieldTex[w.team[i] === Team.Enemy ? 1 : 0]);
      sp.anchor.set(0.5, 0.5);
      this.shieldSprites[i] = sp;
      this.actorLayer.addChild(sp);
    }
    // 화면에서의 방향. 시트 그림은 `pickFrame`이 이미 정했고, 옛 그림은 여기서 고른다.
    let d = fr ? (this.dir[i] as Dir) : this.facing(i);
    // 방패는 무기를 안 든 손에 든다. 시트 그림이면 지금 보이는 칸의 그 손 자리(`UnitFrames.hands`, 그림에서 찾은 값)에
    // 둔다. 처음에는 방향마다 몸 가운데에서 고정한 몇 픽셀에 두었는데, 인물·걸음마다 손 자리가 달라 방패가 창 쥔 손
    // 앞이나 허공에 떴다(사용자: "방패를 무기를 안 든 손이 위치한 곳에 놓아줘"). 피격 칸은 정면 그림이라 정면 손을 쓴다.
    let hand: HandPos | null = null;
    if (fr) {
      const t = body.texture;
      if (t === fr.hit || t === fr.hit2) {
        d = Dir.Front;
        hand = fr.hands.stand[Dir.Front];
      } else hand = t === fr.walkA[d] ? fr.hands.walkA[d] : t === fr.walkB[d] ? fr.hands.walkB[d] : fr.hands.stand[d];
    }
    // 손을 모를 때(옛 코드 그림, 손을 못 찾은 칸)의 자리. 화면 픽셀, 발에서 위로. 병사 그림이 반 배율이라 몸 가운데가
    // 발 위 6~7픽셀쯤이다.
    // 오른쪽 보기 병사 그림은 왼쪽 보기를 좌우로 뒤집은 것이라(`art/tools/roster.py`의 MIRROR_RIGHT_FROM_LEFT) 방패
    // 그림도 뒤집는다. 처음 그림은 오른쪽 보기를 따로 그려 무기를 몸 뒤에 쥔 인물이 많았다.
    let view: ShieldView = ShieldView.Front;
    let sx = 3;
    let sy = -6;
    let behind = false;
    let flip = false;
    if (d === Dir.Left) sx = -2;
    else if (d === Dir.Right) {
      sx = 2;
      flip = true;
    } else if (d === Dir.Back) {
      // 등 뒤에서는 몸이 방패를 가린다. 처음에는 앞 레이어에 두어 등 픽셀 위로 방패 안쪽 면이 덮였다(리뷰가 짚었다).
      view = ShieldView.Back;
      sx = -3;
      behind = true;
    }
    if (hand) {
      sx = hand.dx * SHEET_SCALE;
      sy = hand.dy * SHEET_SCALE;
    }
    if (this.shieldSheet) {
      const spec = w.specs[w.specIdx[i]];
      const tex = this.shieldSheet.texture(spec.culture, spec.shieldBig === true, view);
      if (sp.texture !== tex) sp.texture = tex;
      sp.scale.set(SHEET_SCALE);
    } else sp.scale.set(1);
    if (flip) sp.scale.x = -sp.scale.x;
    // 화면 오른쪽·아래 → 월드. 세로 화면은 그대로, 가로 화면은 화면 아래가 월드 +x, 화면 오른쪽이 월드 -y다.
    const ox = this.rotated ? sy : sx;
    const oy = this.rotated ? -sx : sy;
    sp.rotation = body.rotation;
    sp.x = px + ox;
    sp.y = py + oy;
    sp.zIndex = body.zIndex + (behind ? -0.5 : 0.5);

    if (cur < seen && cur >= 0 && this.sparkBudget > 0) {
      // 막았다.
      this.sparkBudget--;
      this.spark(sp.x, sp.y);
    }
    if (seen > 0 && cur <= 0) {
      this.shieldBrokeAt[i] = now;
      for (let k = 0; k < 2 && this.sparkBudget > 0; k++) {
        this.sparkBudget--;
        this.spark(sp.x + (k === 0 ? -3 : 3), sp.y + 2);
      }
    }
    this.shieldSeen[i] = cur;

    if (cur > 0) {
      sp.visible = true;
      sp.alpha = 1;
      sp.rotation = body.rotation;
      return;
    }
    // 부서지는 중: 기울며 떨어지고 옅어진다.
    const t = now - this.shieldBrokeAt[i];
    if (this.shieldBrokeAt[i] === 0 || t > SHIELD_BREAK_MS) {
      this.shieldBrokeAt[i] = 0;
      sp.visible = false;
      return;
    }
    const k = t / SHIELD_BREAK_MS;
    sp.visible = true;
    sp.alpha = 1 - k;
    sp.rotation = body.rotation + k * 1.2;
    const drop = k * 6;
    sp.x += this.rotated ? drop : 0;
    sp.y += this.rotated ? 0 : drop;
  }

  /**
   * 쓰러진 병사: 쓰러짐 1 → 2 → 3(누움)으로 남았다가 사라진다. 끝났으면 false.
   * 시체는 서 있는 병사 아래에 깐다.
   */
  private syncCorpse(i: number, s: Sprite, fr: UnitFrames, now: number): boolean {
    const t = now - this.deathAt[i];
    if (t > CORPSE_MS + CORPSE_FADE_MS) return false;
    s.texture = t < DEATH_STEP_MS ? fr.death[0] : t < DEATH_STEP_MS * 2 ? fr.death[1] : fr.death[2];
    s.alpha = t > CORPSE_MS ? 1 - (t - CORPSE_MS) / CORPSE_FADE_MS : 1;
    s.zIndex = CORPSE_Z + (this.rotated ? s.x : s.y);
    if (s.tint !== 0xffffff) s.tint = 0xffffff;
    return true;
  }

  private syncUnit(i: number, alpha: number, now: number): void {
    const w = this.world;
    const s = this.spriteFor(i);
    const sh = this.shadows[i]!;
    const fr = this.unitFrames[i] ?? null;
    // 카메라가 돌아도 사람은 서 있다. 발밑 고리(타원)도 눕지 않게.
    const up = this.rotated ? -LANDSCAPE_TURN : 0;
    if (s.rotation !== up) {
      s.rotation = up;
      sh.rotation = up;
    }

    if (w.alive[i] === 0) {
      if (this.wasAlive[i] === 1) {
        this.wasAlive[i] = 0;
        // 빠져나간 병사는 죽은 것이 아니다. 핏자국도 시체도 남기지 않는다. 밤에 보이지 않는 적도 핏자국을 남기지 않는다 —
        // 우리 궁수가 시야 밖 적을 쏘아 쓰러뜨리면 핏자국이 그 자리를 드러냈다(리뷰가 짚었다).
        if (w.exited[i] === 0) {
          if (!this.unseen(i)) this.dropDecal(toPx(w.x[i]), toPx(w.y[i]));
          // 시트 그림이면 쓰러지는 모습을 보인다. 죽은 자리는 마지막 틱의 위치다.
          if (fr) {
            this.deathAt[i] = now;
            s.x = toPx(w.x[i]);
            s.y = toPx(w.y[i]);
          }
        }
        sh.visible = false;
      }
      // 쓰러지거나 빠져나가면 방패도 거둔다.
      const sp = this.shieldSprites[i];
      if (sp && sp.visible) sp.visible = false;
      if (fr && this.deathAt[i] > 0 && this.syncCorpse(i, s, fr, now)) return;
      this.deathAt[i] = 0;
      if (s.visible) {
        s.visible = false;
        sh.visible = false;
      }
      return;
    }
    this.wasAlive[i] = 1;
    s.visible = true;
    sh.visible = true;

    const px = toPx(w.prevX[i] + (w.x[i] - w.prevX[i]) * alpha);
    const py = toPx(w.prevY[i] + (w.y[i] - w.prevY[i]) * alpha);
    s.x = px;
    s.y = py;
    // 앞뒤는 화면의 아래쪽일수록 앞이다. 가로 화면에서 화면의 아래는 월드의 +x다.
    s.zIndex = this.rotated ? px : py;
    sh.x = px - this.upX();
    sh.y = py - this.upY();

    if (w.cd[i] > this.prevCd[i]) {
      // 치는 순간은 타격 칸(`attackHit`)부터 — 준비 칸은 치기 직전에 이미 보였다(`pickFrame`).
      this.attackAt[i] = now - (this.sheet?.attackHit ?? 0) * (this.sheet?.attackMs ?? 0);
      this.attackDir[i] = this.facing(i);
    }
    this.prevCd[i] = w.cd[i];

    if (fr) {
      // 시트 그림은 네 방향을 따로 그렸다. 뒤집지 않는다.
      s.texture = this.pickFrame(i, fr, now);
      if (s.alpha !== 1) s.alpha = 1;
    } else {
      // 예전 코드 그림: 바라보는 쪽으로 뒤집는다. 0일 때는 직전 방향을 유지한다. 화면에서의 좌우로 본다.
      const sx = this.rotated ? -w.faceY[i] : w.faceX[i];
      if (sx > FP_ONE / 4) s.scale.x = 1;
      else if (sx < -FP_ONE / 4) s.scale.x = -1;
    }

    // 새로 맞은 순간에만 불꽃. 프레임당 상한을 둬서 대규모 난전에서도 비용이 고정된다.
    if (w.hitFlash[i] > this.prevFlash[i] && this.sparkBudget > 0) {
      this.sparkBudget--;
      this.spark(px, py);
    }
    this.prevFlash[i] = w.hitFlash[i];

    this.syncShield(i, px, py, s, fr, now);

    // 부대를 고르는 중이면 그 부대원(병사·지휘하는 동료)이 깜빡인다. 장수는 늘 제자리라 빼고.
    if (this.pickMask !== 0 && w.team[i] === Team.Player && w.role[i] !== Role.Leader && (this.pickMask & (1 << w.squad[i])) !== 0) {
      s.alpha = 0.35 + 0.65 * (0.5 + 0.5 * Math.cos(now / 110));
    } else if (s.alpha !== 1) {
      s.alpha = 1;
    }

    if (w.hitFlash[i] > 0) {
      s.tint = 0xff9a9a;
    } else if (w.routed[i] > 0) {
      // 무너져 도망치는 병사는 창백해진다.
      s.tint = 0x9fa8b8;
    } else if (w.withdrawn[i] === 1) {
      // 다쳐서 물러나 추스르는 병사는 풀빛이 돈다. 싸움에서 빠진 것을 한눈에 본다.
      s.tint = 0xa8d8a0;
    } else if (w.rallied[i] > 0) {
      // 함성을 듣고 기세가 오른 병사는 금빛이 돈다.
      s.tint = 0xffe2a0;
    } else if (s.tint !== 0xffffff) {
      s.tint = 0xffffff;
    }
  }

  /**
   * 함성의 기세(`rallied`)가 붙은 병사에게 금빛: 발밑에 숨 쉬듯 커졌다 작아지는 고리, 머리 위로 떠오르는 불티 둘.
   * 기세가 끝나기 1초 전부터 옅어진다. 플레이한 사람이 청했다: "함성에 영향받은 병사들에 버프 비주얼 효과를 붙여줘."
   * 금빛 틴트만으로는 난전 속에서 누가 기세를 받았는지 보이지 않았다. 병사마다 위상을 달리해 한꺼번에 깜빡이지 않게 한다.
   */
  private syncBuffs(now: number): void {
    const w = this.world;
    const ring = this.buffRing;
    const motes = this.buffMotes;
    ring.clear();
    motes.clear();
    const ux = this.upX();
    const uy = this.upY();
    for (let i = 0; i < w.count; i++) {
      if (w.alive[i] === 0 || w.rallied[i] === 0) continue;
      const s = this.sprites[i];
      const sh = this.shadows[i];
      if (!s || !sh || !s.visible) continue;
      const fade = Math.min(1, w.rallied[i] / TICK_HZ);
      const pulse = 0.5 + 0.5 * Math.sin(now / 140 + i * 1.7);
      const r = 6 + 2 * pulse;
      // 고리는 땅에 누운 타원이다. 가로 화면에서는 화면의 세로가 월드의 x라 두 반지름이 바뀐다.
      const rx = this.rotated ? r * 0.55 : r * 1.35;
      const ry = this.rotated ? r * 1.35 : r * 0.55;
      ring.ellipse(sh.x, sh.y, rx, ry).stroke({ width: 1.6, color: 0xffcf4a, alpha: (0.55 + 0.35 * pulse) * fade });
      for (let k = 0; k < 2; k++) {
        const t = (now / 900 + i * 0.37 + k * 0.5) % 1;
        const up = 14 + t * 14;
        const side = k === 0 ? -4 : 4;
        // 옆은 화면의 가로다. 세로 화면이면 월드 x, 가로 화면이면 월드 y.
        const mx = s.x + ux * up + (this.rotated ? 0 : side);
        const my = s.y + uy * up + (this.rotated ? side : 0);
        motes.circle(mx, my, 1.3).fill({ color: 0xffe08a, alpha: fade * (1 - t) });
      }
    }
  }

  /** 함성을 외치면 장수에게서 금빛 고리가 함성이 닿는 거리(`ROAR_RADIUS`)까지 퍼지며 옅어진다. */
  private syncRoar(now: number): void {
    const w = this.world;
    if (w.roarCount !== this.seenRoars) {
      this.seenRoars = w.roarCount;
      this.roarAt = now;
    }
    const g = this.roarGfx;
    g.clear();
    const t = (now - this.roarAt) / 450;
    const l = w.leaderIdx;
    if (t >= 1 || l < 0 || w.alive[l] === 0) return;
    // 가운데는 장수 그림(보간된 자리)이다. 틱 좌표로 그리면 달리는 장수 앞에 떴다(리뷰가 짚었다).
    const s = this.sprites[l];
    if (!s) return;
    // 함성은 닿는 거리(`skillRadius`)까지, 방진·일제사격은 분대에 거는 것이라 장수 둘레의 작은 고리만.
    const reach = w.skillCode === SKILL_CODE.roar ? toPx(w.skillRadius) : 60;
    const r = 18 + (reach - 18) * (1 - (1 - t) * (1 - t));
    g.circle(s.x, s.y, r).stroke({ width: 4 * (1 - t) + 1, color: 0xe8c96a, alpha: 0.9 * (1 - t) });
  }

  /**
   * 둘레에 거는 동료 기술(지장의 계책·충언)이 걸리면 그 동료에게서 푸른 고리가 닿는 거리(`mateRadius`)까지 퍼진다 — 어디까지
   * 걸렸는지가 보여야 지장을 어디에 세울지 고른다. 함성의 고리와 같은 꼴, 다른 색.
   */
  private syncAid(now: number): void {
    const w = this.world;
    const g = this.aidGfx;
    g.clear();
    for (let sq = 0; sq < w.mateRadius.length; sq++) {
      if (w.mateRadius[sq] === 0) continue;
      if (w.mateCount[sq] !== this.seenAid[sq]) {
        this.seenAid[sq] = w.mateCount[sq];
        this.aidAt[sq] = now;
      }
      const t = (now - this.aidAt[sq]) / 600;
      const who = w.mateWho[sq];
      if (t >= 1 || who < 0 || w.alive[who] === 0) continue;
      const s = this.sprites[who];
      if (!s) continue;
      const reach = toPx(w.mateRadius[sq]);
      const r = 18 + (reach - 18) * (1 - (1 - t) * (1 - t));
      g.circle(s.x, s.y, r).stroke({ width: 3 * (1 - t) + 1, color: 0x7fc4e8, alpha: 0.9 * (1 - t) });
    }
  }

  /**
   * 사기가 크게 꺾인 적(함성, 적장의 죽음) 머리 위로 차가운 보랏빛 ▼ 둘이 가라앉으며 옅어진다(`SHAKEN_MS`). 플레이한
   * 사람이 청했다: "함성을 썼을 때 적이 사기가 떨어졌다는 효과를 보여줘야겠어", "사기가 떨어진 경우도 알 수 있게
   * 비주얼적으로 표시해줘." 아군의 기세(금빛, 떠오름)와 반대 색·반대 방향이다. 무너진 적은 원래대로 창백해지며 달아난다.
   *
   * 누가 꺾였는지는 시뮬레이션이 꺾은 순간 적어 둔 틱(`World.shakenTick`)을 읽는다. 처음에는 화면이 그리는 때의
   * 반경으로 다시 셌는데, 외친 뒤 유닛이 움직이고 한 프레임에 틱이 여럿 돌아 실제 대상과 어긋났다(리뷰가 짚었다).
   */
  private syncShaken(now: number): void {
    const w = this.world;
    const g = this.shakenGfx;
    g.clear();
    if (this.shakeSeen.length < w.count) {
      const n = Math.max(w.count, this.shakeSeen.length * 2);
      const seen = new Int32Array(n);
      seen.set(this.shakeSeen);
      const at = new Float64Array(n).fill(-Infinity);
      at.set(this.shakeAt);
      this.shakeSeen = seen;
      this.shakeAt = at;
    }
    const ux = this.upX();
    const uy = this.upY();
    for (let j = 0; j < w.count; j++) {
      const tick = w.shakenTick[j];
      if (tick !== this.shakeSeen[j]) {
        this.shakeSeen[j] = tick;
        this.shakeAt[j] = now;
      }
      const t = (now - this.shakeAt[j]) / SHAKEN_MS;
      if (!(t < 1) || w.alive[j] === 0) continue;
      const alpha = t < 0.15 ? t / 0.15 : 1 - (t - 0.15) / 0.85;
      const s = this.sprites[j];
      if (!s || !s.visible) continue;
      for (let k = 0; k < 2; k++) {
        // 머리 위 26px에서 14px까지 가라앉는다. 둘째는 한 칸 위에서 뒤따른다.
        const lift = 30 - 12 * t + k * 7;
        const cx = s.x + ux * lift;
        const cy = s.y + uy * lift;
        // ▼: 위쪽 두 점과 아래 꼭짓점. 위(`up`)와 옆(`side`)은 화면 기준이라 가로 화면에서는 월드 축이 바뀐다.
        const hw = 5.5;
        const hh = 4;
        const sx = -uy;
        const sy = ux;
        const ax = cx + ux * hh - sx * hw;
        const ay = cy + uy * hh - sy * hw;
        const bx = cx + ux * hh + sx * hw;
        const by = cy + uy * hh + sy * hw;
        const tx = cx - ux * hh;
        const ty = cy - uy * hh;
        g.poly([ax, ay, tx, ty, bx, by], false).stroke({ width: 4, color: 0x1a1430, alpha: alpha * 0.55 * (k === 0 ? 1 : 0.7) });
        g.poly([ax, ay, tx, ty, bx, by], false).stroke({ width: 2.2, color: 0xa99cff, alpha: alpha * (k === 0 ? 1 : 0.7) });
      }
    }
  }

  /**
   * 부대 고르기 중이면 고른 부대의 지휘관 발밑에 금빛 고리를 숨 쉬듯 그린다. "지휘관에서 끌면 경로"의 그 자리다 —
   * 카메라가 전장 끝에 막혀 지휘관이 화면 가운데에 못 오면 어디서 끌지 찾기 어려웠다.
   */
  private syncPickRing(now: number): void {
    const g = this.pickGfx;
    g.clear();
    const w = this.world;
    // 고르는 중이면 고른 지휘관, 아니면 조종하는 동료(장수를 몰 때는 그리지 않는다 — 늘 장수다). 조종하는 동료는
    // 숨 쉬지 않는 고리로 — 누구를 몰고 있는지 난전 속에서도 보이게.
    const f = this.pickFocus();
    const c = w.controlled();
    const u = f >= 0 ? f : c >= 0 && c !== w.leaderIdx ? c : -1;
    if (u < 0) return;
    const x = toPx(w.x[u]);
    const y = toPx(w.y[u]);
    const pulse = f >= 0 ? 0.5 + 0.5 * Math.cos(now / 140) : 0.6;
    g.circle(x, y, 13 + pulse * 3).stroke({ width: 2, color: 0xe8c96a, alpha: 0.55 + 0.45 * pulse });
  }

  /**
   * 장수 휩쓸기의 칼바람: 바라보는 쪽 반원을 칼끝이 한 번 긋고 옅어진다. 휘두를 때마다 긋는 방향을 바꾼다.
   * 반지름은 휩쓸기가 닿는 거리(사거리 + 10px)와 같다 — 그 안에 선 적이 맞는다.
   */
  private syncSlash(now: number): void {
    const w = this.world;
    // 휘두른 사람 자리(`strikeBy` — 장수 또는 조종하는 동료).
    const l = w.strikeBy >= 0 ? w.strikeBy : w.leaderIdx;
    const g = this.slashGfx;
    g.clear();
    if (l < 0 || w.alive[l] === 0) {
      this.slashes.length = 0;
      this.seenStrikes = w.strikeCount;
      return;
    }
    // 휘두른 순간의 방향은 시뮬레이션이 남긴다(`strikeFaceX/Y`). 처음에는 쿨다운이 다시 차는 것을 보고 틱 끝의
    // 방향으로 그렸는데, 수동 휩쓸기는 틱 첫머리에 판정되고 그 뒤 표적 쪽으로 몸을 돌려 엉뚱한 쪽에 그려졌다.
    // 한 프레임에 여러 번 휘둘렀으면(틱이 밀렸을 때) 마지막 한 번만 그린다 — 같은 자리에 겹쳐 그려 봐야 같다.
    if (w.strikeCount !== this.seenStrikes) {
      this.seenStrikes = w.strikeCount;
      this.slashFlip = !this.slashFlip;
      this.slashes.push({
        t0: now,
        ang: Math.atan2(w.strikeFaceY, w.strikeFaceX),
        reach: toPx(w.sRange[l]) + 10,
        flip: this.slashFlip,
      });
    }
    const s = this.sprites[l];
    if (!s) return;
    const cx = s.x + this.upX() * SLASH_LIFT;
    const cy = s.y + this.upY() * SLASH_LIFT;
    for (let k = this.slashes.length - 1; k >= 0; k--) {
      const sl = this.slashes[k];
      const t = now - sl.t0;
      if (t > SLASH_MS) {
        this.slashes.splice(k, 1);
        continue;
      }
      const sweep = Math.min(1, t / SLASH_SWEEP_MS);
      const fade = t <= SLASH_SWEEP_MS ? 1 : 1 - (t - SLASH_SWEEP_MS) / (SLASH_MS - SLASH_SWEEP_MS);
      const from = sl.ang + (sl.flip ? Math.PI / 2 : -Math.PI / 2);
      const span = (sl.flip ? -Math.PI : Math.PI) * sweep;
      const to = from + span;
      const ccw = span < 0;
      // 바깥 칼끝(밝은 금빛)과 안쪽 잔상(옅은 흰빛) 두 겹.
      for (const [r, width, color, a] of [
        [sl.reach, 3, 0xffe9a8, 0.95],
        [sl.reach * 0.72, 2, 0xffffff, 0.45],
      ] as const) {
        g.moveTo(cx + Math.cos(from) * r, cy + Math.sin(from) * r);
        g.arc(cx, cy, r, from, to, ccw);
        g.stroke({ width, color, alpha: a * fade });
      }
    }
  }

  /** 이번 프레임에 체력이 줄어든 병사마다 데미지 숫자를 띄운다. 적이 입으면 금빛(내가 준 피해), 내 편이 입으면 붉은빛. */
  private syncDamage(now: number): void {
    const w = this.world;
    const f = this.floats;
    f.beginFrame();
    this.dmgCursor = scanDamage(w, this.hpSeen, this.dmgCursor, (i, other, leader) => {
      // 밤에 보이지 않는 적의 피해 숫자는 띄우지 않는다 — 우리 궁수는 보이는 거리보다 멀리 쏘아, 숫자가 자리를 드러냈다.
      if (this.unseen(i)) return true;
      const x = toPx(w.x[i]);
      const y = toPx(w.y[i]);
      const color = w.team[i] === Team.Enemy ? DAMAGE_DEALT_COLOR : DAMAGE_TAKEN_COLOR;
      // 장수 숫자는 한도 없이 뜬다. 다른 이의 몫이 한도에 걸리면 false — 그 몫만 다음 프레임으로 미뤄진다.
      // 장수의 한 방에 쓰러졌으면 번쩍인다. 장수 몫은 미루지 않으므로, 쓰러뜨린 틱의 장수 몫은 늘 여기서 처음 뜬다.
      if (leader > 0) f.spawn(x, y, leader, DAMAGE_LEADER_COLOR, now, true, w.leaderKilled[i] === 1);
      return f.spawn(x, y, other, color, now);
    });
    f.update(now, (x, y, out) => {
      this.worldToScreen(x, y, out);
      out.x /= ART_ZOOM;
      out.y /= ART_ZOOM;
    });
  }

  /**
   * 아군 병사 머리 위의 작은 체력 막대. 적과 영웅(장수·동료)은 긋지 않는다 — 적 수백 명에
   * 막대를 달면 전장이 막대로 덮이고, 장수 체력은 아래 줄에 따로 있다.
   */
  private drawHpBars(): void {
    const w = this.world;
    const g = this.hpGfx;
    g.clear();
    const at = this.hpAt;
    for (let i = 0; i < w.count; i++) {
      if (w.alive[i] === 0 || w.team[i] !== Team.Player || w.role[i] !== Role.Soldier) continue;
      const s = this.sprites[i];
      if (!s || !s.visible) continue;
      const pct = Math.max(0, Math.min(1, w.hp[i] / w.maxHp[i]));
      // 스프라이트 윗부분은 투명한 여백이라 꼭대기보다 조금 아래(머리 위)에 둔다. "위"는 화면의 위다.
      // 높이는 텍스처 높이로 잰다 — 돌려 세운 스프라이트의 bounds는 폭과 높이가 뒤바뀐다.
      const head = s.texture.height * Math.abs(s.scale.y) * s.anchor.y - HP_BAR_TOP;
      this.worldToScreen(s.x + this.upX() * head, s.y + this.upY() * head, at);
      const z = this.zoom;
      const x = Math.round(at.x - (HP_BAR_W * z) / 2);
      const y = Math.round(at.y);
      g.rect(x - z, y - z, (HP_BAR_W + 2) * z, 4 * z).fill({ color: 0x15130e, alpha: 0.75 });
      const color = pct > 0.6 ? 0x7fc35a : pct > 0.3 ? 0xe0b43c : 0xe0573c;
      g.rect(x, y, Math.max(z, Math.round(HP_BAR_W * pct) * z), 2 * z).fill({ color });
    }
  }

  private syncArrows(alpha: number): void {
    const w = this.world;
    const g = this.lobShadows;
    g.clear();
    for (let p = 0; p < MAX_PROJECTILES; p++) {
      const s = this.arrows[p];
      if (w.pjAlive[p] === 0) {
        s.visible = false;
        continue;
      }
      s.visible = true;
      s.x = toPx(w.pjPrevX[p] + (w.pjX[p] - w.pjPrevX[p]) * alpha);
      s.y = toPx(w.pjPrevY[p] + (w.pjY[p] - w.pjPrevY[p]) * alpha);
      if (w.pjLob[p] === 1) {
        // 곡사는 포물선으로 띄운다(`lobArc`). 머리는 오를 때 위로, 내릴 때 아래로 기운다. 땅에는 그림자를 남긴다 —
        // 화면 위쪽으로 쏘면 띄우는 쪽과 가는 쪽이 같아 띄우기만으로는 높이가 읽히지 않는다.
        const arc = lobArc(w.pjLife[p], w.pjTtl[p], alpha);
        // 둥근 점으로 그린다 — 납작한 타원은 가로 화면에서(전장을 돌려 보인다) 세로로 길쭉해 보였다(QA).
        g.circle(s.x, s.y + 1, 2.4).fill({ color: 0x15130e, alpha: 0.34 - 0.14 * arc.high });
        s.y -= arc.lift;
        s.rotation = Math.atan2(toPx(w.pjVY[p]) - arc.rise, toPx(w.pjVX[p]));
        // 길이는 기울기를 따라(양 끝에서 짧게, 꼭짓점에서 온 길이), 굵기는 높이를 따라 조금만 는다(`lobScale`).
        const sc = lobScale(arc.high);
        s.scale.set(sc.len, sc.wide);
        continue;
      }
      s.scale.set(1);
      s.rotation = Math.atan2(w.pjVY[p], w.pjVX[p]);
    }
  }

  /** 이동경로. 남은 지점을 선으로 잇고 끝점에 깃발을 세운다. 지난 지점은 지운다. */
  private syncBanners(): void {
    const w = this.world;
    const g = this.pathGfx;
    g.clear();
    for (let sq = 0; sq < MAX_SQUADS; sq++) {
      let s = this.banners[sq] ?? null;
      const len = w.sqPathLen[sq];
      if (len === 0) {
        if (s) s.visible = false;
        continue;
      }
      const base = sq * MAX_WAYPOINTS;
      const last = base + len - 1;
      if (!s) {
        s = new Sprite(this.bannerTex[sq]);
        s.anchor.set(0.5, 0.9);
        this.banners[sq] = s;
        this.actorLayer.addChild(s);
      }
      s.visible = true;
      s.x = toPx(w.sqPathX[last]);
      s.y = toPx(w.sqPathY[last]);
      s.zIndex = this.rotated ? s.x : s.y;
      s.rotation = this.rotated ? -LANDSCAPE_TURN : 0;

      const color = SQUAD_COLORS[sq];
      g.moveTo(toPx(w.sqPathX[base + w.sqPathAt[sq]]), toPx(w.sqPathY[base + w.sqPathAt[sq]]));
      for (let k = base + w.sqPathAt[sq] + 1; k <= last; k++) g.lineTo(toPx(w.sqPathX[k]), toPx(w.sqPathY[k]));
      g.stroke({ width: 3, color, alpha: 0.9 });
      for (let k = base + w.sqPathAt[sq]; k < last; k++) {
        g.circle(toPx(w.sqPathX[k]), toPx(w.sqPathY[k]), 3).fill({ color, alpha: 0.9 });
      }
    }
    // 고르는 중(서 있는 동안) 그린 경로. 새 경로의 첫 지점(`start`)부터. 색은 고른 부대(여럿이면 금빛).
    const pend = this.pendingPath;
    if (this.pickMask !== 0 && pend && pend.length > 0) {
      let from = 0;
      for (let k = 0; k < pend.length; k++) if (pend[k].start) from = k;
      const m = this.pickMask;
      const color = (m & (m - 1)) === 0 ? SQUAD_COLORS[31 - Math.clz32(m)] : 0xe8c96a;
      g.moveTo(pend[from].x, pend[from].y);
      for (let k = from + 1; k < pend.length; k++) g.lineTo(pend[k].x, pend[k].y);
      g.stroke({ width: 3, color, alpha: 0.9 });
      for (let k = from; k < pend.length; k++) g.circle(pend[k].x, pend[k].y, 3).fill({ color, alpha: 0.9 });
    }
  }

  private dropDecal(x: number, y: number): void {
    const s = this.decals[this.decalCursor];
    this.decalCursor = (this.decalCursor + 1) % MAX_DECALS;
    s.visible = true;
    s.x = x;
    s.y = y;
    s.rotation = Math.random() * Math.PI * 2;
    s.alpha = 0.85;
  }

  /** 타격 지점에 불꽃을 튀긴다. 연출 전용이라 시뮬레이션과 무관하다. */
  private spark(x: number, y: number): void {
    const s = this.sparks[this.sparkCursor];
    this.sparkLife[this.sparkCursor] = SPARK_FRAMES;
    this.sparkCursor = (this.sparkCursor + 1) % MAX_SPARKS;
    s.visible = true;
    s.alpha = 1;
    s.x = x + this.upX() * 6 + (Math.random() * 6 - 3);
    s.y = y + this.upY() * 6 + (Math.random() * 4 - 2);
  }

  private fadeSparks(): void {
    for (let i = 0; i < MAX_SPARKS; i++) {
      const life = this.sparkLife[i];
      if (life <= 0) continue;
      const next = life - 1;
      this.sparkLife[i] = next;
      if (next === 0) this.sparks[i].visible = false;
      else this.sparks[i].alpha = next / SPARK_FRAMES;
    }
  }

  /** 끄는 손가락. 있으면 카메라가 장수 대신 손가락 앞을 본다(`lookTarget`). */
  lookAhead: DragLook | null = null;
  /**
   * 부대를 고르는 중(일시정지)이면 그 부대들(비트 마스크). 카메라가 장수 대신 그 부대의 지휘관을 보고, 그 부대원이
   * 깜빡인다(`BattleControls`의 고르기). 0이면 평소대로.
   */
  pickMask = 0;
  /**
   * 아직 틱에 실리지 않은 경로 지점(`InputState.pendingPath`, 월드 픽셀). 부대 고르기로 게임이 서 있는 동안 그린
   * 경로는 풀릴 때까지 월드에 없으므로 여기서 미리 그린다 — 안 그리면 보이지 않는 선을 그리는 셈이었다.
   */
  pendingPath: readonly { x: number; y: number; start: boolean }[] | null = null;
  /** 게임이 서 있는가(부대 고르기). 서 있으면 걷기 그림을 멈춘다 — 안 멈추면 제자리에서 발만 굴렀다. */
  frozen = false;
  /**
   * 다시 보기(`game/replay-player.ts`)의 카메라. 있으면 장수를 따르는 여느 카메라 대신 이것을 따른다: `unit`(전장 색인)을
   * 따라가거나, -1이면 손으로 옮긴 자리(`panBy`)에 선다. 배율도 보는 사람이 정한다(`zoom`) — 맞붙기 전의 물러난 장면
   * (`ApproachView`)은 조종하는 사람을 위한 것이라 여기서는 쓰지 않는다.
   */
  spectate: { unit: number; zoom: number } | null = null;

  /** 싸우는 동안의 여느 배율(폰 1.5, 넓은 화면 2). 다시 보기가 처음 배율로 쓴다. */
  get baseZoom(): number {
    return ZOOM;
  }

  /** 전장이 화면에 다 들어오는 배율(위아래의 옅은 가장자리는 뺀다). 다시 보기의 "전체". */
  fitZoom(): number {
    const sw = this.rotated ? this.app.screen.height : this.app.screen.width;
    const sh = this.rotated ? this.app.screen.width : this.app.screen.height;
    return Math.min(sw / this.world.grid.widthPx, sh / this.world.grid.heightPx);
  }

  /** 보는 카메라를 화면 픽셀만큼 민다(끈 손가락을 따라 땅이 움직인다). 전장 밖으로는 `followCamera`가 가둔다. */
  panBy(dxScreen: number, dyScreen: number): void {
    if (this.camCX < 0) return;
    const [wx, wy] = screenDirToWorld(this.rotated, dxScreen, dyScreen);
    this.camCX -= wx / this.zoom;
    this.camCY -= wy / this.zoom;
  }

  /** 보는 카메라를 전장 한가운데로. */
  centerOnField(): void {
    this.centerOn(this.world.grid.widthPx / 2, this.world.grid.heightPx / 2);
  }

  /** 보는 카메라의 가운데(월드 픽셀). 아직 첫 프레임 전이면 null. 되감아 판을 새로 열 때 보던 자리를 잇는 데 쓴다. */
  camCenter(): { x: number; y: number } | null {
    return this.camCX < 0 ? null : { x: this.camCX, y: this.camCY };
  }

  /** 보는 카메라를 그 자리(월드 픽셀)로 바로 옮긴다. */
  centerOn(x: number, y: number): void {
    this.camCX = Math.max(0, x);
    this.camCY = Math.max(0, y);
  }

  /** 고른 부대에서 카메라가 볼 유닛: 부하 부대 하나면 그 지휘관, 아니면 장수. 없으면 -1. */
  pickFocus(mask = this.pickMask): number {
    const m = mask;
    if (m === 0) return -1;
    const w = this.world;
    if ((m & (m - 1)) === 0) {
      const sq = 31 - Math.clz32(m);
      const c = w.sqCommander[sq];
      if (sq > 0 && c >= 0 && w.alive[c] === 1) return c;
    }
    return w.leaderIdx;
  }
  /**
   * 카메라가 보는 화면 가운데(월드 픽셀). 처음에는 왼쪽 위 모서리를 따라가게 했는데, 배율이 바뀌면(경로 끌기) 모서리를
   * 붙든 채 커지고 줄어 화면이 한쪽으로 쏠렸다. 가운데를 붙들면 제자리에서 넓어진다. 음수면 아직 첫 프레임 전.
   */
  private camCX = -1;
  private camCY = -1;
  /** 지금 배율. 끄는 동안 `DRAG_ZOOM`으로, 놓으면 `ZOOM`으로 부드럽게 옮겨 간다. */
  private zoom = ZOOM;

  /** 다가오는 장면의 상태(`render/approach.ts`) — 끝났는지와 끝난 뒤의 걸음. 판마다 새로 선다. */
  private readonly approach = new ApproachView();
  /** 보는 사람이 향한 쪽을 더 보여 주는 값(`render/face-look.ts`)과, 그 값을 재던 사람(바뀌면 다시 시작한다). */
  private readonly faceLook = new FaceLook();
  private faceOf = -1;

  private followCamera(alpha: number): void {
    const w = this.world;
    const l = w.leaderIdx;
    if (l < 0) return;
    // 보는 사람: 부대를 고르는 중이면 그 지휘관을, 아니면 조종하는 사람(장수 또는 고른 분대의 동료, `controlled`)을 본다.
    const sp = this.spectate;
    // 보는 카메라가 따라갈 사람이 없으면(-1) 그 자리에 선다.
    const u = this.watched();
    // 맞붙기 전에는 적 대열이 함께 보이게 화면을 물린다(`ApproachView`). 경로를 그리는 동안은 그 배율이 앞선다.
    // 장면은 **보는 사람**을 기준으로 잰다 — 처음에는 늘 장수로 쟀는데, 동료를 골라 몰면 카메라가 장수와 적 사이에 남아
    // 모는 동료가 화면 밖으로 나갔다(리뷰가 재현했다).
    // 화면 픽셀을 전장의 가로·세로 쪽으로 읽는다. 가로 화면이면 화면의 세로가 전장의 가로다.
    const sw = this.rotated ? this.app.screen.height : this.app.screen.width;
    const sh = this.rotated ? this.app.screen.width : this.app.screen.height;
    const scene = this.lookAhead || sp ? null : this.approach.scene(w, u, sw, sh, ZOOM);
    const want = sp ? sp.zoom : this.lookAhead ? DRAG_ZOOM : scene ? scene.zoom : ZOOM;
    // 장면이 끝난 뒤 잠깐은 걸음을 천천히 올린다(`APPROACH_EASE_FRAMES`).
    const ease = sp ? 1 : this.approach.ease();
    // 첫 프레임은 바로 그 배율로 연다 — 평소 배율에서 물러나는 것을 보이면 판을 열자마자 화면이 출렁인다.
    this.zoom = this.camCX < 0 || Math.abs(want - this.zoom) < 0.005 ? want : this.zoom + (want - this.zoom) * ZOOM_LERP * ease;
    this.camera.scale.set(this.zoom);
    // 멀리 물러나면(다시 보기의 "전체") 피해 숫자를 걷는다 — 숫자는 화면 크기 그대로라 사람보다 커져 싸움을 덮었다.
    if (sp) this.floats.layer.visible = this.zoom >= SPECTATE_FLOAT_ZOOM;
    // 월드에서 본 화면의 너비(x)와 높이(y). 가로 화면이면 화면의 세로가 월드의 x다.
    const viewW = (this.rotated ? this.app.screen.height : this.app.screen.width) / this.zoom;
    const viewH = (this.rotated ? this.app.screen.width : this.app.screen.height) / this.zoom;
    let px = this.camCX;
    let py = this.camCY;
    let lerp = FOLLOW_LERP;
    let hold = false;
    if (this.lookAhead) {
      // 끄는 방향은 화면에서 쟀다. 월드로 돌려 앞을 본다.
      const [dirX, dirY] = screenDirToWorld(this.rotated, this.lookAhead.dirX, this.lookAhead.dirY);
      const look = this.rotated ? { ...this.lookAhead, dirX, dirY } : this.lookAhead;
      // 앞을 보는 거리는 평소 배율의 화면으로 잰다. 축소한 화면으로 재면 1.6배 멀리 보려 해 카메라가 그만큼 빨리
      // 달렸다 — 축소를 넣자 "경로 지정 시 속도가 너무 빠르다"는 말이 나왔다.
      const k = this.zoom / ZOOM;
      const aim = lookTarget(look, performance.now(), viewW * k, viewH * k);
      // 손가락이 서 있으면 카메라도 선다. 배율은 계속 옮겨 가므로 가운데를 붙든 채 넓어진다.
      hold = aim.still && this.camCX >= 0;
      if (!hold) {
        px = aim.x;
        py = aim.y;
      }
      lerp = LOOK_LERP;
    } else if (u >= 0) {
      px = toPx(w.prevX[u] + (w.x[u] - w.prevX[u]) * alpha);
      py = toPx(w.prevY[u] + (w.y[u] - w.prevY[u]) * alpha);
      // 다가오는 장면: 보는 사람에게서 적 쪽으로 볼 곳을 옮긴다(앞뒤로만 — 옆은 그대로 따른다). 보는 사람이 제 쪽 화면
      // 끝에서 밀려나지 않는 데까지만(`approachShift`).
      if (scene) py += scene.dir * approachShift(scene.gap, viewH);
      // 평소(다가오는 장면이 끝난 뒤): 보는 사람이 향한 쪽을 더 본다(`FaceLook`). 다가오는 장면은 이미 적 쪽을 본다 —
      // 그동안에도 값은 따라가게 해 두어, 장면이 끝날 때 가운데에서 새로 미끄러지지 않는다. 다시 보기의 보는 카메라는
      // 제 규칙(`spectate`)대로다.
      if (u !== this.faceOf) {
        this.faceOf = u;
        this.faceLook.reset();
      }
      const facing = this.faceLook.step(w.faceX[u], w.faceY[u]);
      if (!scene && !sp) {
        const ahead = faceShift(facing, viewW, viewH);
        px += ahead.x;
        py += ahead.y;
      }
      lerp *= ease;
    } else if (this.camCX < 0) {
      // 따라갈 사람이 없는 보는 카메라(자유)가 자리를 잃었다 — 화면을 돌리면 그렇다(`orient`). 전장 가운데에서 연다.
      px = w.grid.widthPx / 2;
      py = w.grid.heightPx / 2;
    }

    // 가운데가 갈 수 있는 범위. 전장이 화면보다 좁으면 전장 가운데. 위아래는 가장자리 너머 옅어지는 땅까지 보여
    // 준다 — 거기가 출구라는 것이 보여야 한다.
    const W = w.grid.widthPx;
    const loY = -EDGE_BLEED + viewH / 2;
    const hiY = w.grid.heightPx + EDGE_BLEED - viewH / 2;
    const clampX = (x: number): number => (viewW >= W ? W / 2 : Math.min(Math.max(x, viewW / 2), W - viewW / 2));
    const clampY = (y: number): number => (loY >= hiY ? (loY + hiY) / 2 : Math.min(Math.max(y, loY), hiY));
    // 부드럽게 따라간다. 끌기를 시작하거나 놓을 때 화면이 튀지 않게. 첫 프레임만 바로 간다.
    // 렌더링 전용이라 시뮬레이션과 무관하다.
    if (this.camCX < 0) {
      this.camCX = clampX(px);
      this.camCY = clampY(py);
    } else if (!hold) {
      this.camCX += (clampX(px) - this.camCX) * lerp;
      this.camCY += (clampY(py) - this.camCY) * lerp;
    }
    // 배율이 바뀌면 갈 수 있는 범위도 바뀐다. 지금 배율에서 전장 밖이 보이지 않게 다시 가둔다.
    this.camCX = clampX(this.camCX);
    this.camCY = clampY(this.camCY);

    const o = cameraOrigin(this.rotated, this.camCX - viewW / 2, this.camCY - viewH / 2, viewH, this.zoom);
    this.camera.x = Math.round(o.x);
    this.camera.y = Math.round(o.y);
  }

  /** 화면 크기(픽셀). 화면 밖 표시가 쓴다. */
  screenSize(): { w: number; h: number } {
    return { w: this.app.screen.width, h: this.app.screen.height };
  }

  /** 전투가 끝나면 픽시 자원을 되돌려준다. 출정을 반복해도 메모리가 쌓이지 않게 한다. */
  destroy(): void {
    this.app.destroy(true, { children: true, texture: true });
  }

  /** 화면 좌표를 월드 픽셀 좌표로. 군기를 꽂을 때 쓴다. */
  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return turnScreenToWorld(this.rotated, this.camera.x, this.camera.y, this.zoom, sx, sy);
  }
}
